<script setup lang="ts">
import type { ImgHTMLAttributes } from 'vue';
import type { Image, ImageFocalPoints } from '~/types';

type Props = {
  image: Image;
  format?: string;
  quality?: string | number;
  background?: string;
  fit?: string;
  modifiers?: Record<string, unknown>;
  preset?: string;
  provider?: string;
  sizes?: string;
  preload?: boolean;
  width?: string | number;
  height?: string | number;
  alt?: string;
  referrerpolicy?: ImgHTMLAttributes['referrerpolicy'];
  crossorigin?: 'anonymous' | 'use-credentials' | boolean;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'auto' | 'sync';
  isDecorative?: boolean;
  role?: 'presentation' | string;
};

const props = withDefaults(defineProps<Props>(), {
  quality: 80,
  provider: 'imageSharp',
  loading: 'lazy',
  format: 'webp',
  isDecorative: false,
  role: undefined,
});

const root = useRoot();
const possibleDuplicateProps = ['width', 'height', 'modifiers', 'alt'];

const imageUrl = computed(() => {
  return props.image?.url ? props.image.url : root.settings.content.generalFallbackImage?.url;
});

let internalSizes: { [key: string]: string } = {};

if (props.sizes) {
  internalSizes = {};
  const screens = useNuxtApp().$img.options.screens;
  if (typeof props.sizes === 'string') {
    for (const entry of props.sizes.split(/[\s,]+/).filter(Boolean)) {
      const s = entry.split(':');
      if (s[0] && s[1]) {
        internalSizes[s[0].trim()] = s[1].trim();
      }
    }
  }
  else {
    Object.assign(internalSizes, props.sizes);
  }

  const maxSizeIndex = Object.keys(internalSizes).reduce((maxIndex, key) => {
    const index = Object.keys(screens).indexOf(key);

    if (index > maxIndex) {
      maxIndex = index;
    }

    return maxIndex;
  }, 0);

  Object.keys(screens).forEach((key, index) => {
    if (index < maxSizeIndex && !internalSizes[key]) {
      internalSizes[key] = '100vw';
    }
  });
}

function mergeImageProperties(props: Props): Omit<Props, keyof typeof possibleDuplicateProps> & {
  width?: string | number;
  height?: string | number;
  alt?: string;
  focalPoint?: ImageFocalPoints;
} {
  const mergedProps: Props & {
    role?: 'presentation' | string;
  } = {
    image: props.image,
  };

  Object.entries(props).forEach(([key, value]) => {
    if (!possibleDuplicateProps.includes(key)) {
      (mergedProps as Record<string, unknown>)[key] = value;
    }
  });

  const width = props.width ?? props.image.width;
  const height = props.height ?? props.image.height;

  // This is to enforce decorative images
  // for more context: https://bitbucket.org/novicell/cshf.frontend.headless/pull-requests/156
  const alt = props.isDecorative ? '' : (props.alt ?? props.image.additionalProperties.alt);
  const role = props.isDecorative ? 'presentation' : props.role;

  const modifierCenterValue
    = props.modifiers?.center ?? `${props.image.focalPoint?.left},${props.image.focalPoint?.top}`;

  const modifierModeValue = props.modifiers?.mode ?? 'crop';

  mergedProps.width = width;
  mergedProps.height = height;
  mergedProps.alt = alt;
  mergedProps.role = role;
  if (modifierCenterValue && modifierModeValue) {
    mergedProps.modifiers = {
      mode: modifierModeValue,
      center: modifierCenterValue,
    };
  }

  return mergedProps;
}

const defaultProps = reactive({
  ...mergeImageProperties(props),
  src: useCdnBase(imageUrl.value || ''),
  internalSizes,
});
</script>

<template>
  <NuxtImg v-bind="defaultProps" />
</template>
